import { useFuzzySearchList } from '@nozbe/microfuzz/react';
import { isEmpty, isNotEmpty, isNotNil } from 'rambda';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { Virtuoso } from 'react-virtuoso';
import { useDebounceValue } from 'usehooks-ts';
import type { ModalAction } from '@/components/common/modal/modal.type';
import SearchInput from '@/components/common/search-input';
import EmptyLayout from '@/components/layouts/empty-layout';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import SearchListItem from '@/components/search/search-list-item';
import TagNameModal from '@/components/tags/tag-name-modal';
import useCreateTag from '@/hooks/tags/use-create-tag';
import useDeleteTags from '@/hooks/tags/use-delete-tags';
import useTags from '@/hooks/tags/use-tags';
import ROUTES from '@/routes';
import { makePrompt } from '@/stores/prompter';
import cn from '@/utils/merge-class-names';

export default function ContactTagsIndexRoute() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState<string>('');
  const { tags } = useTags();
  const [isEditing, setIsEditing] = useState(false);
  const [selectedTags, setSelectedTags] = useState<number[]>([]);

  const { mutateAsync: createTag } = useCreateTag();
  const { mutateAsync: deleteTags } = useDeleteTags();
  const tagNameModalRef = useRef<ModalAction>(null);
  const [debouncedKeyword] = useDebounceValue(keyword, 300);
  const filteredTags = useFuzzySearchList({
    list: tags,
    queryText: debouncedKeyword,
    getText: (item) => [item.TagName].filter(isNotNil),
    mapResultItem: ({ item }) => item,
  });

  const handleSelectTag = (tagId: number) => {
    if (selectedTags.includes(tagId)) {
      setSelectedTags(selectedTags.filter((id) => id !== tagId));
    } else {
      setSelectedTags([...selectedTags, tagId]);
    }
  };

  const handleCreateTag = async (tagName: string) => {
    const res = await createTag(tagName);
    const tagId = res.Data?.Id;
    if (tagId) {
      tagNameModalRef.current?.close();
      navigate(ROUTES.CONTACT.TAGS.BY_ID.$buildPath({ params: { tagId } }));
    }
  };

  const handleDeleteTags = async () => {
    await makePrompt({
      key: 'delete-tags',
      title: t('contact.tags.delete.delete-tag'),
      content: t('contact.tags.delete.confirm-message'),
      promise: () => deleteTags(selectedTags),
    });
    setSelectedTags([]);
    setIsEditing(false);
  };

  return (
    <PageLayout
      title={t('contact.tags.title')}
      headerConfig={{
        navigateBack: true,
        actions: isNotEmpty(tags) && (
          <button
            type='button'
            className='btn btn-ghost btn-sm text-primary mode-embedded:text-primary-content'
            onClick={() => {
              setIsEditing(!isEditing);
              setSelectedTags([]);
            }}>
            {isEditing ? t('common.cancel') : t('common.edit')}
          </button>
        ),
        elementAfterNav: isNotEmpty(tags) && (
          <SearchInput
            className='mx-5'
            keyword={keyword}
            setKeyword={setKeyword}
            clearKeyword={() => setKeyword('')}
          />
        ),
      }}>
      <div className='flex flex-col flex-1 '>
        <Virtuoso
          className='flex-1'
          data={filteredTags}
          components={{
            EmptyPlaceholder: () => <EmptyLayout label='contact.tags.no-content' />,
          }}
          itemContent={(_, tag) => {
            const isSelected = selectedTags.includes(tag.Id);
            const content = (
              <SearchListItem
                className='min-h-12 pl-1'
                avatarImageUrl=''
                titleClassName='desktop:text-xs text-[1.0625rem]'
                title={`${tag.TagName} (${tag.TagCount})`}
                description={tag.TagMember.map(({ Name }) => Name).join(', ')}
                highlight={keyword}
                onClick={() => handleSelectTag(tag.Id)}
                suffix={
                  <input
                    type='checkbox'
                    readOnly
                    checked={isSelected}
                    className={cn(' rounded-full border-base-200 radio-primary radio pointer-events-none', {
                      hidden: !isEditing,
                    })}
                  />
                }
              />
            );

            return isEditing ? (
              content
            ) : (
              <Link to={ROUTES.CONTACT.TAGS.BY_ID.$buildPath({ params: { tagId: tag.Id } })}>{content}</Link>
            );
          }}
        />
      </div>

      <button
        className='btn btn-primary btn-md mx-5'
        disabled={isEditing && isEmpty(selectedTags)}
        onClick={() => {
          isEditing ? handleDeleteTags() : tagNameModalRef.current?.open();
        }}>
        {isEditing ? `${t('common.delete')} (${selectedTags.length})` : t('common.add')}
      </button>

      <TagNameModal
        ref={tagNameModalRef}
        type='create'
        onSubmit={handleCreateTag}
      />
    </PageLayout>
  );
}
